import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import aboutImg1 from '../../assets/images/WhatsApp Image 2026-01-08 at 12.14.34.jpeg';
import aboutImg2 from '../../assets/images/WhatsApp Image 2026-01-08 at 12.14.35.jpeg';
import ministryLogo from '../../assets/images/وزارة_قطاع_الأعمال_العام.png';
import aboutImg3 from '../../assets/images/About-us-imgs/P-IMG-20230613-WA0001.jpg';
import styles from './About.module.css';
import API_BASE_URL from '../../config/api';

const About = () => {
  const { t, i18n } = useTranslation();
  const [about, setAbout] = useState(null);
  const [mission, setMission] = useState([]);
  const [vision, setVision] = useState([]);
  const [targets, setTargets] = useState([]);

  useEffect(() => {
    const controller = new AbortController();

    const fetchAbout = async () => {
      try {
        const lang = i18n.language === 'ar' ? 'ar' : 'en';
        const res = await fetch(
          `${API_BASE_URL}/about?lang=${lang}`,
          { signal: controller.signal },
        );
        const data = await res.json();

        setAbout(data.page || data);
        setMission(data.mission || []);
        setVision(data.vision || []);
        setTargets(data.targets || []);
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Failed to load about page', error);
        }
      }
    };

    fetchAbout();

    return () => controller.abort();
  }, [i18n.language]);

  return (
    <div className={styles.aboutPage}>
      {/* Hero Section with Background Image */}
      <section className={styles.heroSection}>
        <div className={styles.heroOverlay}></div>
        <div className={styles.heroContent}>
          <h1 className={styles.heroTitle}>{about?.heroTitle || t('about.heroTitle')}</h1>
          <div className={styles.heroUnderline}></div>
        </div>
      </section>

      {/* Company Intro with Image - Split Layout */}
      <section className={styles.introSection}>
        <div className={styles.container}>
          <div className={styles.splitLayout}>
            <div className={styles.imageColumn}>
              <div className={styles.imageWrapper}>
                <div className={styles.imagesStack}>
                  <img src={aboutImg1} alt="Al Nasr Forging Factory" className={styles.stackedImage} />
                  <img src={aboutImg2} alt="Al Nasr Forging Operations" className={styles.stackedImage} />
                  <img src={ministryLogo} alt="Ministry of Public Business Sector" className={styles.stackedImage} />
                </div>
                <div className={styles.imageDecoration}></div>
              </div>
            </div>
            <div className={styles.contentColumn}>
              <div className={styles.sectionBadge}>{about?.heroTitle || t('about.heroTitle')}</div>
              <h2 className={styles.largeTitle}>{about?.introTitle || t('about.introTitle')}</h2>
              <p className={styles.introText}>
                {about?.intro || t('about.intro')}
              </p>
              <div className={styles.statsGrid}>
                <div className={styles.statItem}>
                  <div className={styles.statNumber}>{about?.establishedYear || 1960}</div>
                  <div className={styles.statLabel}>{t('about.established')}</div>
                </div>
                <div className={styles.statItem}>
                  <div className={styles.statNumber}>{about?.yearsExperience ?? '60+'}</div>
                  <div className={styles.statLabel}>{t('about.yearsExperience')}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision Cards */}
      <section className={styles.missionVisionSection}>
        <div className={styles.container}>
          <div className={styles.cardsGrid}>
            {/* Mission Card */}
            <div className={styles.featureCard}>
              <div className={styles.cardIcon}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 11l3 3L22 4" />
                  <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" />
                </svg>
              </div>
              <h3 className={styles.cardTitle}>{about?.missionTitle || t('about.missionTitle')}</h3>
              <ul className={styles.cardList}>
                {mission.map((item, index) => (
                  <li key={index}>{item.text}</li>
                ))}
              </ul>
            </div>

            {/* Vision Card */}
            <div className={styles.featureCard}>
              <div className={styles.cardIcon}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              </div>
              <h3 className={styles.cardTitle}>{about?.visionTitle || t('about.visionTitle')}</h3>
              <ul className={styles.cardList}>
                {vision.map((item, index) => (
                  <li key={index}>{item.text}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Company Message with Image - Reversed Split Layout */}
      <section className={styles.messageSection}>
        <div className={styles.container}>
          <div className={styles.splitLayout + ' ' + styles.splitLayoutReverse}>
            <div className={styles.contentColumn}>
              <div className={styles.sectionBadge}>{about?.messageTitle || t('about.messageTitle')}</div>
              <h2 className={styles.largeTitle}>{about?.messageSubtitle || t('about.messageSubtitle')}</h2>
              <p className={styles.messageText}>
                {about?.message || t('about.message')}
              </p>
            </div>
            <div className={styles.imageColumn}>
              <div className={styles.imageWrapper}>
                <img src={aboutImg3} alt="Al Nasr Forging Operations" className={styles.mainImage} />
                <div className={styles.imageDecoration}></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Target Section - Timeline Style */}
      <section className={styles.targetSection}>
        <div className={styles.container}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>{about?.targetTitle || t('about.targetTitle')}</h2>
            <p className={styles.sectionSubtitle}>{about?.targetSubtitle || t('about.targetSubtitle')}</p>
          </div>
          <div className={styles.targetGrid}>
            {targets.map((item, index) => (
              <div key={index} className={styles.targetCard}>
                <div className={styles.targetNumber}>{String(index + 1).padStart(2, '0')}</div>
                <p className={styles.targetText}>{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
